import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
	CurrencyDollarIcon,
	CalendarDaysIcon,
	UsersIcon,
	UserIcon,
	CheckCircleIcon,
	DevicePhoneMobileIcon,
	ExclamationTriangleIcon,
	LinkIcon,
	HandRaisedIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import AppLayout from '../../components/layout/AppLayout';
import { Alert, AppLoader } from '../../components/ui';
import { PhoneVerificationModal } from '../../components/providers';
import useAuthStore from '../../stores/authStore';
import useProviderProfileStore from '../../stores/providerProfileStore';
import type { DashboardAppointment, DashboardActivity } from '../../types/provider';

// Navigation state type
interface LocationState {
	passwordJustChanged?: boolean;
}

// Helper to format currency
const formatCurrency = (amount: number): string => {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).format(amount);
};

// Helper to get initials from name (returns null if name is empty)
const getInitials = (name: string | null | undefined): string | null => {
	if (!name || name.trim() === '') return null;
	const initials = name
		.trim()
		.split(' ')
		.filter(word => word.length > 0)
		.map(word => word[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);
	return initials || null;
};

// Avatar colors for appointments
const avatarColors = [
	'bg-purple-500',
	'bg-blue-500',
	'bg-green-500',
	'bg-orange-500',
	'bg-pink-500',
	'bg-cyan-500',
	'bg-indigo-500',
	'bg-rose-500',
];

const getAvatarColor = (index: number): string => {
	return avatarColors[index % avatarColors.length];
};

const getStatusBadgeClass = (status: string) => {
	switch (status) {
		case 'CONFIRMED':
			return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
		case 'PENDING':
			return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
		case 'COMPLETED':
			return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
		case 'CANCELLED':
			return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
		default:
			return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
	}
};

const getStatusLabel = (status: string): string => {
	switch (status) {
		case 'CONFIRMED':
			return 'Confirmed';
		case 'PENDING':
			return 'Pending';
		case 'COMPLETED':
			return 'Completed';
		case 'CANCELLED':
			return 'Cancelled';
		default:
			return status;
	}
};

const ProviderDashboard = () => {
	const { user } = useAuthStore();
	const navigate = useNavigate();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const {
		profile,
		dashboard,
		selectedLocationId,
		isLoadingProfile,
		isLoadingDashboard,
		profileError,
		dashboardError,
		fetchProfile,
		fetchDashboard,
		setPhoneVerified,
	} = useProviderProfileStore();
	const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);
	const [isPhoneVerificationOpen, setIsPhoneVerificationOpen] = useState(false);

	// Check if password was just changed (from navigation state)
	const passwordJustChanged = locationState?.passwordJustChanged ?? false;

	// Fetch profile if not already loaded (bootstrap handles initial fetch)
	useEffect(() => {
		if (!profile && !isLoadingProfile) {
			fetchProfile();
		}
	}, [profile, isLoadingProfile, fetchProfile]);

	// Fetch dashboard data when selectedLocationId changes
	useEffect(() => {
		if (selectedLocationId) {
			fetchDashboard(selectedLocationId);
		}
	}, [selectedLocationId, fetchDashboard]);

	const dismissAlert = (id: string) => {
		setDismissedAlerts(prev => [...prev, id]);
	};

	const showAlert = (id: string) => !dismissedAlerts.includes(id);

	// Retry fetching all data
	const handleRetry = () => {
		fetchProfile();
		if (selectedLocationId) {
			fetchDashboard(selectedLocationId);
		}
	};

	// Show fullscreen loader while loading initial data (both profile and dashboard)
	const isInitialLoading =
		(isLoadingProfile && !profile) || (isLoadingDashboard && !dashboard && selectedLocationId);
	if (isInitialLoading) {
		return <AppLoader message="Loading dashboard" variant="fullscreen" />;
	}

	// Build stats from dashboard data
	const dashboardStats = [
		{
			id: 'revenue',
			value: dashboard ? formatCurrency(dashboard.todayRevenue) : '$0',
			label: "Today's Revenue",
			icon: CurrencyDollarIcon,
			colorClass: 'bg-gradient-to-br from-green-100 to-green-200',
		},
		{
			id: 'appointments',
			value: dashboard ? dashboard.todayAppointmentsCount.toString() : '0',
			label: "Today's Appointments",
			icon: CalendarDaysIcon,
			colorClass: 'bg-gradient-to-br from-blue-100 to-blue-200',
			link: '/providers/appointments',
		},
		{
			id: 'stylists',
			value: dashboard?.totalStylists?.toString() || '0',
			label: 'Total Stylists',
			icon: UsersIcon,
			colorClass: 'bg-gradient-to-br from-purple-100 to-purple-200',
			link: '/providers/staff-management',
		},
		{
			id: 'customers',
			value: dashboard?.totalCustomers?.toString() || '0',
			label: 'Total Customers',
			icon: UserIcon,
			colorClass: 'bg-gradient-to-br from-orange-100 to-orange-200',
		},
	];

	// Get today's appointments from dashboard
	const appointments: DashboardAppointment[] = dashboard?.todayAppointments || [];

	// Get recent activities (first 5 only)
	const recentActivities: DashboardActivity[] = (dashboard?.recentActivity || []).slice(0, 5);

	// Combined error message
	const error = profileError || dashboardError;

	return (
		<AppLayout>
			{/* Welcome Banner */}
			<div className="bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white p-6 md:p-10 rounded-2xl mb-6 shadow-lg">
				<h2 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-3">
					Welcome{user?.name ? `, ${user.name}` : ''}!
					<HandRaisedIcon className="w-7 h-7 inline-block" />
				</h2>
				<p className="text-base opacity-95">
					Here's what's happening with your salon today.
				</p>
			</div>

			{/* Error Alert */}
			{error && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Unable to Load Data"
					message={error}
					actions={[{ label: 'Retry', onClick: handleRetry, primary: true }]}
					onDismiss={() => {}}
				/>
			)}

			{/* Alerts Section */}
			{/* Password Change Success Alert - only shown when password was just changed */}
			{passwordJustChanged && showAlert('password-changed') && (
				<Alert
					type="success"
					icon={<CheckCircleIconSolid className="w-8 h-8" />}
					title="Password Changed Successfully!"
					message="Your password has been updated. You can now access all features of your provider account."
					onDismiss={() => dismissAlert('password-changed')}
				/>
			)}

			{showAlert('phone-verify') && profile && !profile.phoneVerified && (
				<Alert
					type="warning"
					icon={<DevicePhoneMobileIcon className="w-8 h-8" />}
					title="Verify Your Phone Number"
					message="Please verify your phone number to secure your account and enable SMS notifications for appointments."
					actions={[
						{
							label: 'Verify Now',
							onClick: () => setIsPhoneVerificationOpen(true),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('phone-verify')}
				/>
			)}

			{showAlert('setup-payment') && profile && !profile.hasBankAccount && (
				<Alert
					type="warning"
					icon={<ExclamationTriangleIcon className="w-8 h-8" />}
					title="Complete Your Setup"
					message="Please complete your payment information setup to activate all features and start accepting appointments."
					actions={[
						{
							label: 'Setup Payment Now',
							onClick: () => navigate('/providers/payment-setup'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('setup-payment')}
				/>
			)}

			{showAlert('integrations') && profile && !profile.hasIntegration && (
				<Alert
					type="info"
					icon={<LinkIcon className="w-8 h-8" />}
					title="Complete Your Integration"
					message="Connect your booking platforms like Trendit or Book for Braids to sync appointments and manage everything in one place."
					actions={[
						{
							label: 'Setup Integrations',
							onClick: () => navigate('/providers/integrations'),
							primary: true,
						},
						{ label: 'Later', onClick: () => {}, dismissOnClick: true },
					]}
					onDismiss={() => dismissAlert('integrations')}
				/>
			)}

			{/* Stats Grid */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
				{dashboardStats.map(stat => (
					<div
						key={stat.id}
						className={`bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-[#667eea]${stat.link ? ' cursor-pointer' : ''}`}
						onClick={() => stat.link && navigate(stat.link)}
					>
						<div className="flex justify-between items-start md:mb-4">
							<div>
								<div className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-1">
									{stat.value}
								</div>
								<div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
									{stat.label}
								</div>
							</div>
							<div
								className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${stat.colorClass}`}
							>
								<stat.icon className="w-6 h-6 text-gray-700" />
							</div>
						</div>
					</div>
				))}
			</div>

			{/* Content Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Today's Appointments */}
				<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
					<div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
						<h2 className="text-base font-bold text-gray-900 dark:text-white">
							Today's Appointments
						</h2>
						<Link
							to="/providers/appointments"
							className="text-[#667eea] hover:text-[#764ba2] font-medium text-sm transition-colors"
						>
							View all →
						</Link>
					</div>
					<div className="overflow-x-auto md:px-4">
						{appointments.length === 0 ? (
							<div className="p-8 text-center">
								<CalendarDaysIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
								<p className="text-sm text-gray-500 dark:text-gray-400">
									No appointments for today
								</p>
							</div>
						) : (
							<table className="w-full">
								<thead>
									<tr className="border-b border-gray-200 dark:border-gray-700">
										<th className="text-left py-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-normal">
											Customer
										</th>
										<th className="text-left py-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-normal">
											Service
										</th>
										<th className="text-left py-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-normal">
											Time
										</th>
										<th className="text-left py-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-normal">
											Status
										</th>
									</tr>
								</thead>
								<tbody>
									{appointments.map((appointment, index) => (
										<tr
											key={appointment.id}
											className="group border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gradient-to-r hover:from-purple-50 hover:to-violet-50 dark:hover:from-purple-900/20 dark:hover:to-violet-900/20 cursor-pointer transition-all"
										>
											<td className="py-4">
												<div className="flex items-center gap-3">
													<div
														className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${getAvatarColor(index)}`}
													>
														{getInitials(appointment.customerName) || (
															<UserIcon className="w-4 h-4" />
														)}
													</div>
													<span className="font-medium text-sm text-gray-800 dark:text-gray-200">
														{appointment.customerName || 'Unknown'}
													</span>
												</div>
											</td>
											<td className="py-4 text-sm text-gray-600 group-hover:text-gray-800 dark:text-gray-300 dark:group-hover:text-gray-100 transition-colors">
												{appointment.serviceName || '-'}
											</td>
											<td className="py-4 text-sm text-gray-600 group-hover:text-gray-800 dark:text-gray-300 dark:group-hover:text-gray-100 transition-colors">
												{appointment.time || '-'}
											</td>
											<td className="py-4">
												<span
													className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(appointment.status)}`}
												>
													{getStatusLabel(appointment.status)}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</div>

				{/* Recent Activity */}
				<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden">
					<div className="p-4 border-b border-gray-200 dark:border-gray-700">
						<h2 className="text-base font-bold text-gray-900 dark:text-white">
							Recent Activity
						</h2>
					</div>
					{recentActivities.length === 0 ? (
						<div className="p-8 text-center">
							<CheckCircleIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
							<p className="text-sm text-gray-500 dark:text-gray-400">
								No recent activity
							</p>
						</div>
					) : (
						<div className="divide-y divide-gray-100 dark:divide-gray-700">
							{recentActivities.map((activity, index) => (
								<div
									key={index}
									className="flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
								>
									<span className="text-lg flex-shrink-0 mt-0.5">
										{activity.icon}
									</span>
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{activity.title}
										</p>
										<p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
											{activity.description}
										</p>
									</div>
									<span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
										{activity.time}
									</span>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Phone Verification Modal */}
			<PhoneVerificationModal
				isOpen={isPhoneVerificationOpen}
				onClose={() => setIsPhoneVerificationOpen(false)}
				phoneNumber={profile?.locations?.find(loc => loc.primary)?.phone || undefined}
				onVerified={() => {
					// Update phoneVerified flag in profile store
					setPhoneVerified(true);
					// Dismiss the phone verification alert
					dismissAlert('phone-verify');
					// Show success toast
					toast.success('Phone number verified successfully!');
				}}
			/>
		</AppLayout>
	);
};

export default ProviderDashboard;
